import React, { useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { extractErrorMessage } from '../../utils/extractErrorMessage'
import { useGetCompanyMappingOptions } from '../../api/company-mapping'
import { useGetGmnPoint } from '../../api/gmn'
import { NoOrdersAlert } from '../shared/NoOrdersAlert'
import { TotalPoints } from '../shared/TotalPoints'
import { TicketOverrideTable } from '../shared/TicketOverrideTable'
import { AuditSubtitle } from '../shared/AuditSubtitle'

const numberField = yup
  .number()
  .transform((value, originalValue) =>
    originalValue === '' || originalValue === null ? undefined : value,
  )
  .typeError('Must be a number')
  .integer('Must be a whole number')
  .min(0, 'Must be positive')
  .nullable()
  .notRequired()

// One row per ticket type, so each earned value sits beside the field that overrides it.
const TICKET_ROWS = [
  { label: 'Tier 1 Tickets', id: 't1Tickets' },
  { label: 'Tier 2 Tickets', id: 't2Tickets' },
  { label: 'Multi-Product Ticket', id: 'multiProductTicket' },
]

const validationSchema = yup.object().shape({
  mappingId: yup.string().required('Company is required'),
  bonusT2Tickets: numberField,
  ticketOverrides: yup
    .object()
    .shape(Object.fromEntries(TICKET_ROWS.map(({ id }) => [id, numberField]))),
})

/** Stored nulls become empty inputs, so a blank field reads as "no override". */
const toOverrideInputs = (overrides) =>
  Object.fromEntries(TICKET_ROWS.map(({ id }) => [id, overrides?.[id] ?? '']))

/** Form values for a company, with its overrides shaped for controlled inputs. */
const toFormValues = (defaultValues) => ({
  bonusT2Tickets: 0,
  ...defaultValues,
  ticketOverrides: toOverrideInputs(defaultValues?.ticketOverrides),
})

const Form = ({ isEdit, defaultValues, setOpen, open, api, refetch }) => {
  const { control, handleSubmit, reset, setValue } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: toFormValues(defaultValues),
  })

  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync, isLoading } = api()
  const { data: cmOptions = [], isLoading: isLoadingCMOptions } = useGetCompanyMappingOptions()

  // When editing, load the company's computed points/tickets to display.
  const { data: points } = useGetGmnPoint(
    { customerId: defaultValues?.mappingId },
    { enabled: Boolean(open && isEdit && defaultValues?.mappingId) },
  )

  /**
   * What a ticket type earns on its own. A dash until points arrive — that endpoint needs its own
   * permission, and 0 would read as "earns nothing" to an admin who cannot see it.
   */
  const earnedFor = (id) => points?.earnedTickets[id] ?? '—'

  const onClose = () => {
    reset()
    setOpen(false)
  }

  const onSubmit = async (values) => {
    try {
      await mutateAsync(values)
      // Awaited: reopening the dialog before the refetch resolves would show pre-save values.
      await refetch()
      enqueueSnackbar(`Successfully ${isEdit ? 'updated' : 'created'}`, { variant: 'success' })
      onClose()
    } catch (e) {
      enqueueSnackbar(extractErrorMessage(e), { variant: 'error' })
    }
  }

  // Keyed on `open` — Row rebuilds defaultValues every render, so a refetch would reset mid-edit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => reset(toFormValues(defaultValues)), [reset, open])

  return (
    <Dialog maxWidth='md' open={open} fullWidth>
      <DialogTitle>Golden Moon Night</DialogTitle>
      {isEdit && <AuditSubtitle company={defaultValues} />}
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name='mappingId'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    options={cmOptions}
                    getOptionLabel={(option) => `${option.name} (ID: ${option.id})`}
                    value={cmOptions.find((option) => option.id === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue ? newValue.id : '')
                      setValue('name', newValue ? newValue.name : '')
                    }}
                    loading={isLoadingCMOptions}
                    disabled={isEdit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Company'
                        variant='outlined'
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            {isEdit && (
              <Grid item xs={12}>
                <NoOrdersAlert points={points} />
                <TotalPoints value={points?.totalPoints} />
                <TicketOverrideTable control={control} rows={TICKET_ROWS} earnedFor={earnedFor} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Controller
                name='bonusT2Tickets'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label='Bonus Tier 2 Tickets'
                    variant='outlined'
                    fullWidth
                    type='number'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={onClose}>
            CANCEL
          </Button>
          <Button type='submit' variant='contained' color='primary' disabled={isLoading}>
            SUBMIT
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default Form
